<template>
  <div class="event-table">
    <el-table
      :data="participantArray"
      style="width: 100%"
      empty-text="No participants"
    >
      <el-table-column prop="name" label="Participants" :width="250">
        <template #default="scope">
          <router-link
            :to="{
              name: 'UserProfile',
              params: { link: scope.row.link },
            }"
          >
            <div class="event-table__user">
              <el-avatar class="event-table__avatar" :size="50">
                <user-avatar :userId="scope.row.userId" />
              </el-avatar>
              <span class="event-table__name">
                {{ scope.row.name }}
              </span>
            </div>
          </router-link>
        </template>
      </el-table-column>
      <el-table-column prop="role" label="Role" :width="150">
        <template #default="scope">
          <el-select
            v-model="scope.row.role"
            class="wide"
            @change="requestChangeRole(scope.row.participantId, scope.row.role)"
            v-if="isAdmin"
          >
            <el-option
              v-for="role in roleDescriptions"
              :key="role.value"
              :label="role.description"
              :value="role.value"
            >
            </el-option>
          </el-select>

          <div class="event-table__role" v-else>
            {{ scope.row.role }}
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="participantFunctions" label="Functions">
        <template #default="scope">
          <div>
            <el-select
              v-if="isAdmin"
              v-model="scope.row.participantFunctions"
              multiple
              filterable
              allow-create
              default-first-option
              placeholder="Choose functions for participant"
              class="wide"
              @change="
                (val) => handleChangeParticipantFunctions(val, scope.row)
              "
              @remove-tag="
                (tag) => handleRemoveParticipantFunction(tag, scope.row)
              "
            >
              <el-option
                v-for="participantFunction in participantFunctionDescriptions"
                :key="participantFunction.value"
                :label="participantFunction.description"
                :value="participantFunction.value"
              >
              </el-option>
            </el-select>

            <span
              v-else
              class="event-table__function"
              v-for="roleFunction in scope.row.participantFunctionsWithDescriptions"
              :key="roleFunction.value"
            >
              {{ roleFunction.description }}
            </span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="" :width="110" v-if="isAdmin">
        <template #default="scope">
          <el-button
            type="danger"
            plain
            @click="handleDeleteParticipant(scope.row)"
            :disabled="isButtonDisabled('delete-participant')"
          >
            Delete
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      v-model="isDeleteParticipantConfirmationVisible"
      width="30%"
      title="Confirmation"
    >
      <span v-if="participantToDelete">
        Are you sure you want to delete {{ participantToDelete.name }} from this
        event?
      </span>
      <template #footer>
      <span class="dialog-footer">
        <el-button @click="isDeleteParticipantConfirmationVisible = false"
          >Cancel</el-button
        >
        <el-button
          type="danger"
          @click="requestDeleteParticipant(participantToDelete.participantId)"
          >Delete</el-button
        >
      </span>
    </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  getEventParticipants,
  getEventRoleDescriptions,
  changeParticipantRole,
  deleteParticipantFromEvent,
  removeParticipantFunction,
  addParticipantFunction,
  getAchievementFunctionDescriptions,
} from '@/api/events.js';
import { showMessage } from '@/utils/messages.js';
import { disableButton } from '@/mixins/disableButton.js';
import UserAvatar from '@/components/profile/UserAvatar.vue'

export default {
  name: 'EventParticipants',
  components: {
    UserAvatar,
  },
  props: {
    eventId: {
      type: Number,
      required: true,
    },
    isAdmin: {
      type: Boolean,
      required: false,
    },
  },
  mixins: [disableButton],
  data() {
    return {
      participantArray: [],
      roleDescriptions: [],
      participantFunctionDescriptions: [],
      isDeleteParticipantConfirmationVisible: false,
      participantToDelete: {},
    };
  },
  methods: {
    showMessage,
    async fetchEventParticipants() {
      const { eventId } = this;
      const { data, success } = await getEventParticipants({ eventId });

      if (success) {
        this.setData(data.items);
      }
    },
    async fetchRoleDescriptions() {
      const { eventId } = this;
      const { data, success } = await getEventRoleDescriptions({ eventId });

      if (success) {
        this.roleDescriptions = data.items;
      }
    },
    async requestChangeRole(participantId, role) {
      const { success } = await changeParticipantRole({
        participantId,
        eventRoleId: role,
      });

      if (success) {
        this.fetchEventParticipants();
        this.showMessage({
          type: 'success',
          message: 'Participant role sah been updated',
        });
      }
    },
    async requestDeleteParticipant(participantId) {
      this.disableButton('delete-participant');
      const { success } = await deleteParticipantFromEvent({
        eventParticipantId: participantId,
      });
      this.enableButton('delete-participant');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Participant has been deleted',
        });

        this.isDeleteParticipantConfirmationVisible = false;
        this.participantToDelete = null;
        this.fetchEventParticipants();
      }
    },
    async requestRemoveParticipantFunction(participantId, participantFunction) {
      const { success } = await removeParticipantFunction({
        participantId,
        participantFunction,
      });

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Participant function has been removed',
        });

        this.fetchEventParticipants();
      }
    },
    async requestAddParticipantFunction(participantId, participantFunction) {
      const { success } = await addParticipantFunction({
        participantId,
        participantFunction,
      });

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Participant function has been added',
        });

        this.fetchEventParticipants();
      }
    },
    async fetchParticipantFunctionDescriptions() {
      const { success, data } = await getAchievementFunctionDescriptions();

      if (success) {
        this.participantFunctionDescriptions = data.items;
      }
    },
    setData(data) {
      this.participantArray = data.map((participant) => {
        const participantFunctions = participant.functions.map(
          (participantFunction) => participantFunction.value,
        );

        return {
          participantId: participant.participantId,
          name: participant.user.firstName + ' ' + participant.user.lastName,
          userId: participant.user.userId,
          link: participant.user.link,
          role: participant.role,
          participantFunctions: participantFunctions,
          oldParticipantFunctions: participantFunctions,
          participantFunctionsWithDescriptions: participant.functions,
        };
      });
    },
    handleChangeParticipantFunctions(val, participant) {
      const {
        participantId,
        participantFunctions,
        oldParticipantFunctions,
      } = participant;

      const newParticipantFunction = participantFunctions.find(
        (participantFunction) => {
          return oldParticipantFunctions.every(
            (oldParticipantFunction) =>
              oldParticipantFunction !== participantFunction,
          );
        },
      );

      if (newParticipantFunction) {
        this.requestAddParticipantFunction(
          participantId,
          newParticipantFunction,
        );
      }
    },
    handleRemoveParticipantFunction(tag, participant) {
      this.requestRemoveParticipantFunction(participant.participantId, tag);
    },
    handleDeleteParticipant(participant) {
      this.isDeleteParticipantConfirmationVisible = true;
      this.participantToDelete = participant;
    },
  },
  created() {
    this.fetchEventParticipants();
    this.fetchRoleDescriptions();
    this.fetchParticipantFunctionDescriptions();
  },
};
</script>

<style lang="scss">
.event-table {
  padding-top: 0;

  overflow: hidden;
  padding: var(--global-dimension-size-125) var(--global-dimension-size-300)
    var(--global-dimension-size-400);
  background-color: var(--color-gray-50);
  border-radius: var(--border-radius-base);

  &__user {
    display: flex;
    align-items: center;
    flex-flow: row nowrap;
  }

  &__avatar {
    margin-right: var(--global-dimension-size-200);
  }

  &__name {
    font-weight: 500;
    font-size: var(--font-size-200);
    line-height: 1.5;
    vertical-align: middle;
    color: var(--color-gray-900);
  }

  &__expiration-date {
    font-size: var(--font-size-100);
    color: var(--color-gray-800);
    font-weight: normal;
  }

  &__comment {
    word-break: normal;
  }

  &__role {
    text-transform: capitalize;
    font-weight: 500;
    font-size: var(--font-size-100);
    line-height: 1.6;
  }

  &__function {
    text-transform: capitalize;
    word-break: break-word;

    & + & {
      position: relative;
      margin-left: 15px;

      &:before {
        position: absolute;
        top: var(--global-dimension-size-85);
        left: -12px;
        content: '';

        width: var(--global-dimension-size-65);
        height: var(--global-dimension-size-65);

        background-color: var(--color-gray-800);
        border-radius: 50%;
      }
    }
  }
}
</style>
